import { appSettings } from 'config/app.settings';

import { Products } from '../api/assigned-product.types';
import { GTMOrderType } from '../api/order-status.types';

interface ITagPageVisitProps {
  isAuthenticated: boolean;
  billingCustomerHashedId: string;
}

export const getWindow = ({ window }: Window) => window as Window;

export const tagPageVisit = (props: ITagPageVisitProps, windowObj = getWindow(window || {})) => {
  const { isAuthenticated, billingCustomerHashedId } = props;

  try {
    const path = windowObj.location.pathname;

    // Section equals first part of url after /mijnziggo/ or uses 'dashboard' if no path is available
    const section = path.replace(appSettings.ENV_PUBLIC_URL, '').split('/')[0] || 'dashboard';

    windowObj._ddm.trigger('routeChange.ready', {
      data: {
        page: {
          logged_in: isAuthenticated,
          type: section,
          route: path,
        },
        user: {
          products: {},
          other: {},
          relevant: {},
          items: {},
          zcid: billingCustomerHashedId,
        },
      },
    });
    // eslint-disable-next-line no-empty
  } catch (e) {}
};

export interface IClickToggleMeta {
  technicalName: string;
  meaningfulName: string;
}

export const clickToggle = (name: string, value: boolean, meta?: IClickToggleMeta) => {
  try {
    window?._ddm?.trigger('clickToggle', {
      data: {
        event: 'click.toggle',
        type: 'toggle',
        description: name,
        status: value ? 'on' : 'off',
      },
      component: {
        technical_name: meta?.technicalName,
        meaningful_name: meta?.meaningfulName,
        location: window.location.pathname,
        id: undefined,
        type: 'toggle',
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while sending clickToggle event');
  }
};

export const orderStepTracker = (step: string, isAuthenticated: boolean, billingCustomerHashedId?: string) => {
  try {
    const path = window.location.pathname;
    window._ddm.trigger('order.step', {
      data: {
        page: {
          logged_in: isAuthenticated,
          type: path.replace(appSettings.ENV_PUBLIC_URL, '').trim().split('/')[0] || 'dashboard',
          path: path.replace(appSettings.ENV_PUBLIC_URL, '') || 'dashboard',
          step,
        },
        user: {
          zcid: billingCustomerHashedId,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while sending order step event');
  }
};

export const emailInfoTracker = (hasMail: boolean, billingCustomerHashedId?: string) => {
  const path = window.location.pathname;
  try {
    window._ddm.trigger('ziggo_mail_available', {
      data: {
        page: {
          logged_in: true,
          type: path.replace(appSettings.ENV_PUBLIC_URL, '').trim().split('/')[0] || 'dashboard',
          route: path,
        },
        user: {
          zcid: billingCustomerHashedId,
          ziggo_mail: hasMail,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while sending order step event');
  }
};

export const userStreamingServicesTracker = (products: Products, billingCustomerHashedId?: string) => {
  const path = window.location.pathname;
  const hasStreamingServices =
    products.televisionProducts.length > 0 &&
    products.televisionProducts[0].packages.length > 0 &&
    products.televisionProducts[0].packages
      .map((tvPackage) => tvPackage.subscription)
      .filter((subscription) => subscription !== null).length > 0;

  try {
    window._ddm.trigger('has_streaming_service', {
      data: {
        page: {
          logged_in: true,
          type: path.replace(appSettings.ENV_PUBLIC_URL, '').trim().split('/')[0] || 'dashboard',
          route: path,
        },
        user: {
          zcid: billingCustomerHashedId,
          streaming_activation: hasStreamingServices,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while checking if user has streaming services');
  }

  if (hasStreamingServices) {
    const hasNotActivatedStreamingServices =
      products.televisionProducts[0].packages
        .map((tvPackage) => tvPackage.subscription)
        .filter((subscription) => subscription && subscription.OTTstatus === 'notActivated').length > 0;

    try {
      window._ddm.trigger('open_streaming_activation', {
        data: {
          page: {
            logged_in: true,
            type: path.replace(appSettings.ENV_PUBLIC_URL, '').trim().split('/')[0] || 'dashboard',
            route: path,
          },
          user: {
            zcid: billingCustomerHashedId,
            streaming_activation: hasNotActivatedStreamingServices,
          },
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error while checking if user has non-activated streaming services');
    }
  }
};

const parseOrderType = (args: GTMOrderType): string => {
  return Object.keys(args)
    .map((key) => `${key}: ${args[key]}`)
    .join(', ');
};

export const orderTrackerPageVisit = (
  orderType: GTMOrderType,
  orderStepName: string | undefined,
  isAuthenticated: boolean,
  billingCustomerHashedId?: string
) => {
  try {
    window?._ddm?.trigger('fe.ga_event', {
      data: {
        type: 'user_event',
        interaction: {
          event: 'ordertracker',
          type: 'impression',
          status: orderStepName,
          detail: `isAuthenticated: ${isAuthenticated}`.concat(', ').concat(parseOrderType(orderType)),
        },
        component: {
          technical_name: '<ProgressTracker>',
          meaningful_name: 'order_details_progress_block',
          location: 'orderstatus',
          id: 'tracker',
          type: 'widget',
        },
        user: {
          zcid: billingCustomerHashedId,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while sending order tracker page visit event', e);
  }
};

export const getGASessionId = (): string => {
  const key = appSettings.NODE_ENV === 'production' ? '_ga_TH972FDWFF' : '_ga_NJRTNWKZPH';
  const gaCookie = document.cookie.split('; ').find((row) => row.startsWith(key));

  if (gaCookie) {
    const gaValue = gaCookie.split('=')[1];
    if (gaValue) {
      // Sample value: GS2.1.{sessionId}$o14$g0$t1748939859$j50$l0$h0
      return gaValue.split(/[.$]/)[2]?.replace(/[a-z]+/, '') || 'unknown';
    }
  }

  return 'unknown';
};

export const faqTracker = (
  question: string,
  opened: boolean,
  orderStep?: string,
  billingCustomerHashedId?: string,
  orderId?: string
) => {
  try {
    window?._ddm?.trigger('fe.ga_event', {
      data: {
        type: 'user_event',
        interaction: {
          event: 'faq.tracker',
          type: 'click_accordion',
          element_label: opened ? 'open' : 'close',
          element_text: question,
          order_status: orderStep || 'unknown',
          order_id: orderId || 'unknown',
        },
        component: {
          technical_name: '<FaqCard>',
          meaningful_name: 'faq_item',
          location: window.location.pathname,
          id: question,
          type: 'widget',
        },
        user: {
          zcid: billingCustomerHashedId,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while sending faqTracker event', e);
  }
};

export const unsubscribeAndSubscribeTracker = (action: 'unsubscribe' | 'subscribe', type: string, success: boolean) => {
  window?._ddm?.trigger('fe.ga_event', {
    data: {
      type: 'system_event',
      interaction: {
        event: 'update privacy setting',
        type: 'log',
        description: type,
        status: success ? 'success' : 'error',
        detail: action === 'subscribe' ? 'opt-in' : 'opt-out',
      },
      component: {
        location: 'my_zg',
      },
    },
  });
};
